<script lang="ts">
export default defineComponent({
  name: 'CommunityDrawerWrapper',
  inheritAttrs: false,
})
</script>

<script lang="ts" setup>
import { app } from '@/composition/app'
import { DEFAULT_SAFE_AREA_INSET, Platform } from '@/helper/const'
import { checkNativeKeyboardResize } from '@/util/helper'
import { Capacitor, registerPlugin } from '@capacitor/core'
import { UIDrawer } from '@gohighlevel/ghl-ui'
import { defineComponent, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
})

const KeyboardEventsPlugin: any = registerPlugin('KeyboardEvents')

defineEmits(['update:show'])

const drawerHeight = ref(props.height)
const drawerElement = ref<HTMLElement | null>(null)

onMounted(async () => {
  const hasNativeKeyboardResize = await checkNativeKeyboardResize(
    KeyboardEventsPlugin
  )

  if (Capacitor.getPlatform() === Platform.IOS && !hasNativeKeyboardResize) {
    KeyboardEventsPlugin.addListener('keyboardWillShow', info => {
      handleKeyboardWillShow(info.keyboardHeight)
    })

    KeyboardEventsPlugin.addListener('keyboardWillHide', () => {
      handleKeyboardWillHide()
    })
  }

  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.overlaysContent = true
    // @ts-ignore
    navigator.virtualKeyboard.addEventListener(
      'geometrychange',
      handleGeometryChange
    )
  } else if (app.state.isMobile && Capacitor.getPlatform() !== Platform.IOS) {
    //Add event listener to listen to visual viewport changes
    window.visualViewport?.addEventListener(
      'resize',
      handleVisualViewportResize
    )
    // // Handle the scroll which safari makes, when input is highlighted
    window.addEventListener('focus', handleInputFocusOnResize, true)
  }

  nextTick(() => {
    drawerElement.value = document.getElementById(props.id) as HTMLElement
  })
})

const handleKeyboardWillShow = keyboardHeight => {
  setTimeout(() => {
    if (drawerElement.value) {
      window.scrollTo({
        behavior: 'smooth',
        left: 0,
        top: 2,
      })
      drawerElement.value.style.bottom = `${
        keyboardHeight - DEFAULT_SAFE_AREA_INSET
      }px`
    }
  }, 50)
}

const handleKeyboardWillHide = () => {
  if (drawerElement.value) {
    drawerElement.value.style.height = `${props.height}px`
    drawerElement.value.style.bottom = '0px'
  }
}

onBeforeUnmount(() => {
  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.removeEventListener(
      'geometrychange',
      handleGeometryChange
    )
  }

  window.visualViewport?.removeEventListener(
    'resize',
    handleVisualViewportResize
  )

  window.removeEventListener('focus', handleInputFocusOnResize, true)
})

function handleInputFocusOnResize(focusEvent: any) {
  // Only handle focus for input or textarea elements
  const tag = focusEvent?.target?.tagName

  if (!tag || (tag !== 'INPUT' && tag !== 'TEXTAREA')) {
    return
  }

  handleVisualViewportResize()
  nextTick(() => {
    setTimeout(() => {
      window.scrollTo({
        behavior: 'smooth',
        left: 0,
        top: 2,
      })
    }, 100)
  })

  focusEvent.preventDefault()
}
function handleVisualViewportResize() {
  // Keyboard likely is open.
  if (app.state.isMobile) {
    if (
      window.visualViewport &&
      window.visualViewport?.height < window.innerHeight
    ) {
      if (drawerElement.value && window.visualViewport) {
        drawerElement.value.style.bottom = `${
          window.innerHeight - window.visualViewport.height
        }px`

        if (window.visualViewport.height < props.height) {
          drawerElement.value.style.height = `${
            window.visualViewport.height - 50
          }px`
        }
      }
    } else if (window.visualViewport?.height == window.innerHeight) {
      if (drawerElement.value && window.visualViewport) {
        drawerElement.value.style.height = `${props.height}px`
        drawerElement.value.style.bottom = '0px'
      }
    }
  }
}

function handleGeometryChange(changeEvent) {
  const { height } = changeEvent.target.boundingRect
  // embedToolbarBottom.value = height
  if (app.state.isMobile) {
    if (height > 0) {
      // Keyboard is open with height, so adjust the modal height such that it is not hidden behind the keyboard + 75px for the toolbar
      if (drawerElement.value) {
        drawerElement.value.style.bottom = `${height}px`
        const newDrawerHeight = window.innerHeight - height
        if (newDrawerHeight < props.height) {
          drawerElement.value.style.height = `${newDrawerHeight - 50}px`
        }
      }
    } else {
      handleMemberListDrawerHeight()
    }
  }
}

function handleMemberListDrawerHeight() {
  if (drawerElement.value) {
    drawerElement.value.style.bottom = '0px'
    drawerElement.value.style.height = props.height + 'px'
  }
}
</script>
<template>
  <UIDrawer
    v-bind="$attrs"
    :id="id"
    :height="drawerHeight"
    :show="show"
    @update:show="val => $emit('update:show', val)"
  >
    <slot />
  </UIDrawer>
</template>
<style>
.n-drawer-header__main {
  width: 100%;
}
</style>
